/* ──────────────────────────────────────────────
 *  Réserve d'un événement de suivi en attente de confirmation
 *
 *  Un tunnel (réservation, course, carte cadeau) connaît le contenu de la commande au moment où
 *  elle est créée, mais la confirmation peut arriver bien plus tard et sur une page rechargée
 *  (retour de redirection Stripe : Klarna, 3DS). Les paramètres complets de l'événement sont
 *  donc rangés en `sessionStorage` à la création, puis repris UNE fois à la confirmation, quel
 *  que soit le chemin.
 *
 *  Sans réserve, aucun événement : un paiement terminé dans un autre navigateur (application
 *  bancaire qui rouvre le navigateur par défaut) n'a ni le clic publicitaire ni le consentement
 *  de ce navigateur. On ne reconstruit rien depuis le statut du paiement.
 * ────────────────────────────────────────────── */

import {
  minorUnitToSiteEventValue,
  type SiteEventItem,
  type SiteEventParams,
  type SiteEventUserData,
  type TrackingItemObject,
} from '@cfixe-monorepo/shared';

/** Événements émis à la confirmation d'un tunnel. */
export type ReservedSiteEventName = 'booking_confirmed' | 'purchase';

/** Événement prêt à émettre : paramètres du contrat + données saisies par le client. */
export interface ReservedSiteEvent<T extends ReservedSiteEventName> {
  params: SiteEventParams[T];
  userData?: SiteEventUserData;
}

/** Ligne de commande renvoyée par le backend (`tracking_items`, montant brut en centimes). */
export type TrackingItem = TrackingItemObject;

const STORAGE_KEY_PREFIX = 'site_event_reserve:';

function storageKey(key: string): string {
  return `${STORAGE_KEY_PREFIX}${key}`;
}

/** Lignes du backend → `items` du contrat (prix en unité monétaire). */
export function toSiteEventItems(items: TrackingItem[] | null | undefined): SiteEventItem[] {
  return (items ?? []).map(
    (item): SiteEventItem => ({
      item_id: item.item_id,
      item_name: item.item_name,
      price: minorUnitToSiteEventValue(item.price_raw),
      quantity: item.quantity,
    }),
  );
}

/** `service_name` : les noms des lignes, sans doublon, joints par une virgule. */
export function joinItemNames(items: SiteEventItem[]): string {
  return [...new Set(items.map((item): string => item.item_name))].join(', ');
}

export function readSiteEventReserve<T extends ReservedSiteEventName>(key: string): ReservedSiteEvent<T> | undefined {
  try {
    const raw = sessionStorage.getItem(storageKey(key));
    return raw ? (JSON.parse(raw) as ReservedSiteEvent<T>) : undefined;
  } catch {
    return undefined;
  }
}

export function writeSiteEventReserve<T extends ReservedSiteEventName>(key: string, event: ReservedSiteEvent<T>): void {
  try {
    sessionStorage.setItem(storageKey(key), JSON.stringify(event));
  } catch {
    // Stockage indisponible : la commande continue, seul l'événement de suivi sera absent.
  }
}

export function clearSiteEventReserve(key: string): void {
  try {
    sessionStorage.removeItem(storageKey(key));
  } catch {
    // Rien à nettoyer.
  }
}

/**
 * Reprend l'événement à la confirmation et efface la réserve : un rechargement de la page de
 * confirmation ne retrouve plus rien.
 */
export function takeSiteEventReserve<T extends ReservedSiteEventName>(key: string): ReservedSiteEvent<T> | undefined {
  const event = readSiteEventReserve<T>(key);
  if (event) clearSiteEventReserve(key);
  return event;
}
